import React from 'react';
import { useFormikContext, FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import { OutgoingEventProductDto } from 'api/dtos/events/products';
import { Popup } from 'semantic-ui-react';
import { CURRENCY_CODES } from 'legacy/util/constants';
import { UserMe } from 'legacy/user/types';

import styles from '../../../FormStyles.module.scss';
import FormikSelectField from '../FormikSelectField';
import FormikNumberField from '../FormikNumberField';
import { AlertTriangleIcon } from '../icons';
import { FormValueDto } from 'legacy/events/components/create-event/settings/forms/TicketForm/helpers/computeInitialValues';

interface BundleFieldsProps {
  standardProducts: OutgoingEventProductDto[];
  user: UserMe;
  isUnsaved: (field: string) => boolean;
}

const BundleFields: React.FC<BundleFieldsProps> = ({
  standardProducts,
  user,
  isUnsaved
}) => {
  const { t } = useTranslation(['events']);
  const { values, errors, submitCount } = useFormikContext<FormValueDto>();

  return (
    <>
      {/* Common fields customized for Bundle products */}
      <div className="mb-6">
        <Field
          name="name"
          id="name"
          type="text"
          label={`${t('events:pricingTiers.ticketForm.bundleName')}* ${
            isUnsaved('name') ? t('events:pricingTiers.ticketForm.unsaved') : ''
          }`}
          disabled={!values.canDelete}
          placeholder={t(
            'events:pricingTiers.ticketForm.bundleNamePlaceholder'
          )}
        />
        <Field
          type="text"
          id="description"
          name="description"
          label={`${t('events:pricingTiers.ticketForm.description')}* ${
            isUnsaved('description')
              ? t('events:pricingTiers.ticketForm.unsaved')
              : ''
          }`}
          placeholder={t(
            'events:pricingTiers.ticketForm.bundleDescriptionPlaceholder'
          )}
        />
      </div>

      <div className="flex gap-6 mb-6">
        {/* Price field */}
        {!values.canDelete && (
          <span className={styles.tooltip}>
            <Popup
              hoverable
              position="right center"
              trigger={<i className="fa fa-info-circle" />}
              size="tiny"
              content={t('events:pricingTiers.ticketForm.priceCannotBeEdited')}
            />
          </span>
        )}

        <div className="grow">
          <Field
            id="price"
            name="price"
            type="number"
            disabled={!values.canDelete}
            label={`${t('events:pricingTiers.ticketForm.bundlePrice')} (${
              CURRENCY_CODES[user?.country]
            })* ${
              isUnsaved('price')
                ? t('events:pricingTiers.ticketForm.unsaved')
                : ''
            }`}
            placeholder={t('events:pricingTiers.ticketForm.pricePlaceholder')}
          />

          {/* Savings calculation - positioned like validation errors */}
          {(() => {
            // Get products that have been selected
            const selectedProducts =
              values.components
                ?.filter((comp) => comp.productId && comp.quantity)
                .map((comp) => {
                  const product = standardProducts.find(
                    (p) => p.id === comp.productId
                  );
                  return {
                    ...comp,
                    price: product?.price || 0
                  };
                }) || [];

            // Calculate total cost of all individual products
            const totalProductsPrice = selectedProducts.reduce(
              (sum, comp) => sum + comp.price * comp.quantity,
              0
            );

            const bundlePrice = Number(values.price) || 0;
            const savings = totalProductsPrice - bundlePrice;

            if (selectedProducts.length === 0 || !bundlePrice) {
              return (
                <p className="text-[#969696] font-normal text-sm -mt-4">
                  {t('events:pricingTiers.ticketForm.setPriceAndAddTiers')}
                </p>
              );
            }

            return (
              <p
                className={`${
                  savings > 0 ? 'text-[#048300]' : 'text-[#969696]'
                } font-normal text-sm -mt-4`}
              >
                {savings > 0
                  ? t('events:pricingTiers.ticketForm.customerSavings', {
                      amount: savings.toFixed(2)
                    })
                  : t('events:pricingTiers.ticketForm.noSavingsLabel')}
              </p>
            );
          })()}
        </div>
      </div>

      <div className=" mb-2">
        <h3 className="text-lg font-semibold mb-2 text-[#202020]">
          {t('events:pricingTiers.ticketForm.ticketTiersInBundle')}
        </h3>
        <p className="text-sm text-[#575757] font-normal mb-4">
          {t('events:pricingTiers.ticketForm.addTicketTiersDescription')}
        </p>
        <FieldArray name="components">
          {({ push, remove }) => {
            // Get currently selected product IDs to filter them out
            const selectedProductIds =
              values.components?.map((c) => c.productId) || [];

            // Get available products (excluding already selected ones)
            const availableProducts = standardProducts.filter(
              (p) => !selectedProductIds.includes(p.id)
            );

            return (
              <div className="">
                <div className="space-y-4">
                  {values.components?.map((component, index) => {
                    // For each row, we need to include the currently selected product
                    // in the options list, even if it's already selected in another row
                    const productOptions = [
                      ...availableProducts,
                      ...(component.productId
                        ? standardProducts.filter(
                            (p) => p.id === component.productId
                          )
                        : [])
                    ]
                      // Sort all options alphabetically by name
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((product) => ({
                        value: product.id,
                        label: `${product.name} - ($${product.price})`
                      }));

                    // Create a more specific key using component data and index as fallback
                    const componentKey = component.productId
                      ? `${component.productId}-${component.quantity}-${index}`
                      : `new-component-${index}`;

                    return (
                      <div key={componentKey}>
                        <div className="flex gap-2 items-start">
                          <div className="w-full">
                            <FormikSelectField
                              label={t(
                                'events:pricingTiers.ticketForm.ticketTier'
                              )}
                              name={`components.${index}.productId`}
                              options={productOptions}
                              placeholder={t(
                                'events:pricingTiers.ticketForm.selectTicketTier'
                              )}
                              disabled={!values.canDelete}
                            />
                          </div>
                          <div className="max-w-[72px]">
                            <FormikNumberField
                              label={t(
                                'events:pricingTiers.ticketForm.quantity'
                              )}
                              name={`components.${index}.quantity`}
                              placeholder="1"
                              min={1}
                              disabled={!values.canDelete}
                            />
                          </div>
                        </div>
                        <div className="ml-1 mr-1 mt-1 flex items-center">
                          <div className="w-full border-b-1 border-gray-200" />
                          <button
                            type="button"
                            className={`text-sm pl-4 ${
                              !values.canDelete
                                ? 'text-red-300 cursor-not-allowed'
                                : 'text-red-700'
                            }`}
                            onClick={() => remove(index)}
                            disabled={!values.canDelete}
                          >
                            {t('events:pricingTiers.ticketForm.remove')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Array-level errors for components - improved error handling */}
                {Object.keys(errors).filter((key) =>
                  key.startsWith('components')
                ).length > 0 &&
                  submitCount > 0 && (
                    <div className="mt-2 mb-4 text-red-800 flex items-center gap-1 font-normal text-sm">
                      <span className="text-red-800">
                        <AlertTriangleIcon />
                      </span>
                      {typeof errors.components === 'string'
                        ? errors.components
                        : t(
                            'events:pricingTiers.ticketForm.someTicketTiersIncomplete'
                          )}
                    </div>
                  )}

                <button
                  type="button"
                  className={`mt-0 px-3 py-2 text-sm bg-white font-semibold transition-colors duration-200 flex items-center ${
                    availableProducts.length === 0 || !values.canDelete
                      ? 'text-[#8DA0E3] cursor-not-allowed'
                      : 'text-[#3853C4]'
                  }`}
                  disabled={availableProducts.length === 0 || !values.canDelete}
                  onClick={() => push({ productId: '', quantity: 1 })}
                >
                  {t('events:pricingTiers.ticketForm.addAnotherTicketTier')}
                </button>
              </div>
            );
          }}
        </FieldArray>
      </div>
    </>
  );
};

export default BundleFields;
